"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import {
	CLINIC_INQUIRY_MESSAGE_MAX,
	PRIVACY_POLICY_URL,
	TERMS_OF_SERVICE_URL,
} from "@klarify/constants";
import type { ClinicInquirySubmitInput } from "@klarify/schemas";
import { clinicInquirySubmitSchema } from "@klarify/schemas";
import {
	classifyTrafficSource,
	isInquiryCountryAllowed,
} from "@klarify/utilities";
import { Trans, useLingui } from "@lingui/react/macro";
import { Send } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { api } from "../../../trpc/client";
import { Button } from "../../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
} from "../../ui/dialog";
import {
	Form,
	FormControl,
	FormDescription,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../ui/form";
import { Input } from "../../ui/input";
import { PhoneInput } from "../../ui/phone-input";
import { Textarea } from "../../ui/textarea";
import { COUNTRY_OPTIONS } from "../country-options";
import { LocationCombobox } from "../location-combobox";
import { getStateOptions } from "../location-options";
import { initialsFor } from "../shared";
import { TurnstileWidget } from "./turnstile-widget";

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	slug: string;
	firstName: string;
	lastName: string;
	credentialsSuffix: string | null;
	turnstileSiteKey: string;
	referrerPostId?: string | null;
	therapistId?: string;
}

export const ContactTherapistModal = ({
	open,
	onOpenChange,
	slug,
	firstName,
	lastName,
	credentialsSuffix,
	turnstileSiteKey,
	referrerPostId,
	therapistId,
}: Props) => {
	const { t } = useLingui();

	const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
	const [serverError, setServerError] = useState<string | null>(null);
	const [submittedToEmail, setSubmittedToEmail] = useState<string | null>(null);

	const fullName =
		[firstName, lastName].filter(Boolean).join(" ") || t`this therapist`;
	const displayFirst = firstName || t`this therapist`;
	const initials = initialsFor(firstName, lastName);
	const headingFullName = credentialsSuffix
		? `${fullName}, ${credentialsSuffix}`
		: fullName;

	const form = useForm({
		resolver: zodResolver(clinicInquirySubmitSchema),
		defaultValues: {
			slug,
			senderName: "",
			senderEmail: "",
			inquirerCountry: "",
			inquirerState: "",
			senderPhone: "",
			subject: "",
			message: "",
			website: "",
			turnstileToken: "",
		},
	});
	const messageValue = form.watch("message") ?? "";
	const countryValue = form.watch("inquirerCountry") ?? "";
	const stateOptions = useMemo(
		() => getStateOptions(countryValue || null),
		[countryValue],
	);
	// Countries with no subdivisions in the dataset submit a null state; the
	// form only requires a state when the chosen country actually has one.
	const stateRequired = stateOptions.length > 0;

	const submit = api.clinicInquiryRouter.submit.useMutation({
		onSuccess: (data) => {
			if (!data.ok) {
				setServerError(
					t`This therapist isn't currently accepting messages from your region.`,
				);
				return;
			}
			setSubmittedToEmail(form.getValues("senderEmail"));
		},
		onError: (err) => setServerError(err.message),
	});

	// Prefill the location from the visitor's IP (Vercel edge geo) when the
	// modal opens, so most people just confirm rather than pick. Fetched lazily
	// on open so cacheable public profile pages don't turn dynamic for it. The
	// guard leaves anything the visitor has already chosen untouched.
	const visitorGeo = api.clinicInquiryRouter.visitorGeo.useQuery(undefined, {
		enabled: open,
		staleTime: Number.POSITIVE_INFINITY,
	});
	useEffect(() => {
		const geo = visitorGeo.data;
		// Re-run on open so a close (which resets the form) still re-prefills on
		// the next open.
		if (!(open && geo?.country)) {
			return;
		}
		// Preserve anything the visitor has already chosen.
		if (form.getValues("inquirerCountry")) {
			return;
		}
		form.setValue("inquirerCountry", geo.country);
		if (geo.countryRegion) {
			form.setValue("inquirerState", geo.countryRegion);
		}
	}, [open, visitorGeo.data, form]);

	const regionPolicy = api.clinicInquiryRouter.regionPolicy.useQuery(
		{ slug },
		{ enabled: open, staleTime: Number.POSITIVE_INFINITY },
	);
	// Only surface the block once the visitor has an actual country selected —
	// otherwise a restrictive profile would flash the block (and disable Send)
	// on open, before the IP-geo prefill resolves or the visitor picks a
	// country. The server remains the authority regardless.
	const outOfRegion = Boolean(
		regionPolicy.data &&
			countryValue &&
			!isInquiryCountryAllowed(
				regionPolicy.data.allowOutOfRegionInquiries,
				regionPolicy.data.authorizedCountries,
				countryValue,
			),
	);

	const onSubmit = (values: ClinicInquirySubmitInput) => {
		if (!turnstileToken) {
			return;
		}
		// State is conditionally required, so the zod schema can't enforce it
		// without the subdivision dataset — gate it here where we know whether
		// the chosen country has states.
		if (stateRequired && !values.inquirerState) {
			form.setError("inquirerState", {
				message: t`Please select your state or province.`,
			});
			return;
		}
		setServerError(null);
		const source = classifyTrafficSource(
			document.referrer || undefined,
			window.location.hostname,
		);
		submit.mutate({
			...values,
			turnstileToken,
			referrerPostId: referrerPostId ?? null,
			source,
			therapistId,
		});
	};

	const close = () => {
		onOpenChange(false);
		setTimeout(() => {
			form.reset();
			setTurnstileToken(null);
			setSubmittedToEmail(null);
			setServerError(null);
		}, 200);
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => (o ? onOpenChange(true) : close())}
		>
			<DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
				{submittedToEmail ? (
					<div className="flex flex-col gap-4 px-2 py-4 sm:px-4">
						<DialogTitle className="font-semibold text-2xl text-klarify-neutral-900">
							<Trans>Message sent</Trans>
						</DialogTitle>
						<DialogDescription className="text-klarify-neutral-600 text-sm">
							{displayFirst} <Trans>will reply to</Trans>{" "}
							<span className="font-medium text-klarify-neutral-800">
								{submittedToEmail}
							</span>{" "}
							<Trans>. We've also sent you a confirmation copy.</Trans>
						</DialogDescription>
						<div className="flex justify-end">
							<Button onClick={close}>
								<Trans>Close</Trans>
							</Button>
						</div>
					</div>
				) : (
					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit)}
							className="flex min-w-0 flex-col gap-5 px-2 py-2 sm:px-4"
							noValidate
						>
							<div className="flex min-w-0 items-start gap-3 pr-14">
								<div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-klarify-forest-200 font-semibold text-base text-klarify-forest-800">
									{initials}
								</div>
								<div className="flex min-w-0 flex-col gap-0.5">
									<span className="font-medium text-[11px] text-klarify-neutral-500 uppercase tracking-wider">
										<Trans>New message</Trans>
									</span>
									<span className="truncate font-semibold text-base text-klarify-neutral-900">
										<Trans>To</Trans> {headingFullName}
									</span>
								</div>
							</div>

							<div className="flex flex-col gap-1">
								<DialogTitle className="font-semibold text-2xl text-klarify-neutral-900">
									<Trans>Send</Trans> {displayFirst} <Trans>a message</Trans>
								</DialogTitle>
								<DialogDescription className="text-klarify-neutral-600 text-sm">
									<Trans>
										Your message goes straight to {{ name: displayFirst }}'s
										inbox.
									</Trans>
								</DialogDescription>
							</div>

							<div className="border-klarify-neutral-200 border-t" />

							{serverError ? (
								<div
									role="alert"
									className="rounded-md border border-klarify-peach-200 bg-klarify-peach-50 p-3 text-klarify-peach-700 text-sm"
								>
									{serverError}
								</div>
							) : null}

							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
								<FormField
									control={form.control}
									name="senderName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<Trans>Your name</Trans>
											</FormLabel>
											<FormControl>
												<Input
													autoComplete="name"
													placeholder={t`Jane Doe`}
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="senderEmail"
									render={({ field }) => (
										<FormItem>
											<FormLabel>
												<Trans>Email</Trans>
											</FormLabel>
											<FormControl>
												<Input
													type="email"
													autoComplete="email"
													placeholder={t`jane@example.com`}
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
								<FormField
									control={form.control}
									name="inquirerCountry"
									render={() => (
										<FormItem>
											<FormLabel htmlFor="inquirer-country">
												<Trans>Country</Trans>
											</FormLabel>
											<LocationCombobox
												id="inquirer-country"
												placeholder={t`Select country`}
												searchPlaceholder={t`Search countries…`}
												options={COUNTRY_OPTIONS}
												value={countryValue || null}
												onSelect={(id) => {
													form.setValue("inquirerCountry", id, {
														shouldValidate: true,
													});
													form.setValue("inquirerState", "");
												}}
											/>
											<FormDescription>
												<Trans>
													So the therapist can confirm they're able to work with
													you.
												</Trans>
											</FormDescription>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="inquirerState"
									render={({ field }) => (
										<FormItem>
											<FormLabel htmlFor="inquirer-state">
												<Trans>State / province</Trans>
											</FormLabel>
											<LocationCombobox
												id="inquirer-state"
												placeholder={
													stateRequired
														? t`Select state / province`
														: t`Not applicable`
												}
												searchPlaceholder={t`Search regions…`}
												options={stateOptions}
												value={field.value || null}
												disabled={!stateRequired}
												onSelect={(id) =>
													form.setValue("inquirerState", id, {
														shouldValidate: true,
													})
												}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							{outOfRegion ? (
								<div
									role="alert"
									className="rounded-md border border-klarify-peach-200 bg-klarify-peach-50 p-3 text-klarify-peach-700 text-sm"
								>
									<Trans>
										This therapist isn't currently accepting messages from your
										region.
									</Trans>
								</div>
							) : null}

							<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
								<FormField
									control={form.control}
									name="senderPhone"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="flex items-baseline gap-1.5">
												<span>
													<Trans>Phone</Trans>
												</span>
												<span
													aria-hidden="true"
													className="text-klarify-neutral-300"
												>
													·
												</span>
												<span className="font-normal text-klarify-neutral-500 text-xs">
													<Trans>optional</Trans>
												</span>
											</FormLabel>
											<FormControl>
												<PhoneInput
													autoComplete="tel"
													placeholder="(416) 555-0142"
													value={field.value ?? ""}
													onChange={field.onChange}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
								<FormField
									control={form.control}
									name="subject"
									render={({ field }) => (
										<FormItem>
											<FormLabel className="flex items-baseline gap-1.5">
												<span>
													<Trans context="contact therapist form">
														Subject
													</Trans>
												</span>
												<span
													aria-hidden="true"
													className="text-klarify-neutral-300"
												>
													·
												</span>
												<span className="font-normal text-klarify-neutral-500 text-xs">
													<Trans>optional</Trans>
												</span>
											</FormLabel>
											<FormControl>
												<Input
													maxLength={120}
													placeholder={t`Booking an intake session`}
													{...field}
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<FormField
								control={form.control}
								name="message"
								render={({ field }) => (
									<FormItem>
										<div className="flex items-baseline justify-between">
											<FormLabel>
												<Trans>Message</Trans>
											</FormLabel>
											<span className="text-klarify-neutral-400 text-xs tabular-nums">
												{messageValue.length} / {CLINIC_INQUIRY_MESSAGE_MAX}
											</span>
										</div>
										<FormControl>
											<Textarea
												rows={6}
												maxLength={CLINIC_INQUIRY_MESSAGE_MAX}
												placeholder={t`Hi ${{ name: displayFirst }}, I'm reaching out because…`}
												{...field}
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>

							{/* Honeypot — hidden from real users, often filled by bots. */}
							<div
								aria-hidden="true"
								className="sr-only"
							>
								<label>
									<Trans>Website</Trans>
									<input
										type="text"
										tabIndex={-1}
										autoComplete="off"
										{...form.register("website")}
									/>
								</label>
							</div>

							<p className="text-klarify-neutral-500 text-xs leading-relaxed">
								<Trans>By sending, you agree to our</Trans>{" "}
								<a
									href={PRIVACY_POLICY_URL}
									target="_blank"
									rel="noopener noreferrer"
									className="underline underline-offset-2"
								>
									<Trans>Privacy Policy</Trans>
								</a>{" "}
								<Trans>and</Trans>{" "}
								<a
									href={TERMS_OF_SERVICE_URL}
									target="_blank"
									rel="noopener noreferrer"
									className="underline underline-offset-2"
								>
									<Trans>Terms</Trans>
								</a>
								.
							</p>

							{/* Turnstile mounts here so it sits between the consent box and
							    the action buttons. With appearance: "interaction-only" the
							    widget is invisible for legit traffic — placing it after the
							    consent box keeps that empty-space artefact in a less
							    noticeable spot. When a challenge IS shown, it appears right
							    above Send, which is also the natural flow. */}
							<div className="flex justify-center empty:hidden">
								<TurnstileWidget
									siteKey={turnstileSiteKey}
									onToken={(t) => {
										setTurnstileToken(t);
										form.setValue("turnstileToken", t);
									}}
									onExpire={() => {
										setTurnstileToken(null);
										form.setValue("turnstileToken", "");
									}}
								/>
							</div>

							<div className="flex items-center justify-end gap-2">
								<Button
									type="button"
									variant="ghost"
									onClick={close}
								>
									<Trans>Cancel</Trans>
								</Button>
								<Button
									type="submit"
									disabled={!turnstileToken || submit.isPending || outOfRegion}
									className="gap-1.5"
								>
									{submit.isPending ? (
										<Trans>Sending…</Trans>
									) : (
										<>
											<Trans>Send message</Trans>
											<Send className="size-4" />
										</>
									)}
								</Button>
							</div>
						</form>
					</Form>
				)}
			</DialogContent>
		</Dialog>
	);
};
